"use client"

import Link from "next/link"
import { Ellipsis, LogOut } from "lucide-react"
import { usePathname } from "next/navigation"

import { cn } from "@/lib/utils"
import { Button } from "@/components/ui/button"
// import { ScrollArea } from "@/components/ui/scroll-area"
import { CollapseMenuButton } from "@/components/sidebar/collapse-menu-button"
import {
    Tooltip,
    TooltipTrigger,
    TooltipContent,
    TooltipProvider
} from "@/components/ui/tooltip"
import {
    LayoutGrid,
    MessageCircle,
    FileText,
    History,
    Bug,
    MessageSquare,
    Cpu,
    HelpCircle,
    Info,
    ShoppingCart,
    Mail,
    Settings,
} from "lucide-react"
import { Group } from "@/lib/types"
import { useEffect, useRef, useState } from "react"
import { motion } from "motion/react"
import { useRecentConversations } from "@/hooks/use-recent-conversations"
import { useDeveloperFeatures } from "@/hooks/use-developer-feature"
import { isToday, isYesterday, isThisWeek, parseISO } from "date-fns"
import InfiniteScroll from "react-infinite-scroll-component"

export function getMenuList(pathname: string, recentConversations: any[] = [], showDevFeatures: boolean = false): Group[] {
    const menuList = [
        {
            groupLabel: "Dev Mode",
            menus: [
                {
                    href: "/dashboard",
                    label: "Dashboard",
                    icon: LayoutGrid,
                    submenus: []
                },
                {
                    href: "/test",
                    label: "Test",
                    icon: HelpCircle,
                    submenus: []
                },
                {
                    href: "/issues",
                    label: "Issues",
                    icon: Bug,
                    submenus: []
                },
                {
                    href: "/models",
                    label: "Models",
                    icon: Cpu
                },
            ]
        },
        {
            groupLabel: "Main",
            menus: [
                {
                    href: "/files",
                    label: "Files",
                    icon: FileText
                },
                {
                    href: "/history",
                    label: "History",
                    icon: History
                },
                {
                    href: "/settings",
                    label: "Settings",
                    icon: Settings
                }
            ]
        },
        // Placeholder group for history list; items are rendered custom in Menu
        {
            groupLabel: "History",
            menus: []
        }
    ]

    return menuList.filter(menu => {
        if (!showDevFeatures) {
            return menu.groupLabel !== "Dev Mode"
        }
        return true
    })
}

interface MenuProps {
    isOpen: boolean | undefined
    isAdmin: boolean
}

export function Menu({ isOpen, isAdmin }: MenuProps) {
    const pathname = usePathname()
    const { isFeatureEnabled } = useDeveloperFeatures()
    const { conversations, loadMore, hasMore } = useRecentConversations(20)
    const menuList = getMenuList(pathname, conversations, isFeatureEnabled('sidebarLinks') && isAdmin)

    const listRef = useRef<HTMLUListElement>(null)
    const [hoverTop, setHoverTop] = useState<number | null>(null)
    const [hoverHeight, setHoverHeight] = useState<number>(0)

    const handleHover = (buttonRect: DOMRect) => {
        const listRect = listRef.current?.getBoundingClientRect()
        if (!listRect) return
        setHoverTop(buttonRect.top - listRect.top)
        setHoverHeight(buttonRect.height)
    }

    const clearHover = () => setHoverTop(null)

    return (
        <div className="z-[20001] overflow-x-visible h-full pb-2 min-h-0 ">
            <nav className="mt-6 h-full w-full overflow-x-visible">
                <ul
                    ref={listRef}
                    onMouseLeave={clearHover}
                    className={cn("relative flex flex-col h-full items-start px-1.5 overflow-x-visible ")}
                >
                    {hoverTop !== null && (
                        <motion.div
                            aria-hidden
                            className="absolute top-0 left-0 right-0 pointer-events-none"
                            initial={false}
                            animate={{ y: hoverTop, height: hoverHeight, opacity: 1 }}
                            transition={{ y: { type: "spring", stiffness: 600, damping: 40, mass: 0.6 }, opacity: { duration: 0.12 } }}
                            style={{ zIndex: 0, willChange: 'transform,height,opacity' }}
                        >
                            <div className="h-full w-full rounded-md bg-accent" />
                        </motion.div>
                    )}
                    {menuList.map(({ groupLabel, menus }, index) => (
                        <li className={cn("w-full", groupLabel ? "pt-3" : "", groupLabel === 'History' ? "flex-1 min-h-0 flex flex-col" : "")} key={index}>
                            <div className="h-7 ">
                                {(isOpen && groupLabel) || isOpen === undefined ? (
                                    <p className="text-xs font-medium text-muted-foreground px-0 pb-3 max-w-[248px] truncate h-5 ">
                                        {groupLabel}
                                    </p>
                                ) : !isOpen && isOpen !== undefined && groupLabel ? (
                                    <p className="text-xs font-medium text-muted-foreground px-0 pb-3 truncate flex justify-center items-center h-5">
                                        <Ellipsis className="h-5 w-5" />
                                    </p>
                                ) : (
                                    <p className="pb-3 h-5"></p>
                                )}
                            </div>
                            {groupLabel !== 'History' && menus.map(
                                ({ href, label, icon: Icon, active, submenus }, index) => (
                                    <div
                                        className="w-full relative z-[1]"
                                        key={index}
                                        onMouseEnter={(e) => handleHover((e.currentTarget as HTMLDivElement).getBoundingClientRect())}
                                        onMouseMove={(e) => handleHover((e.currentTarget as HTMLDivElement).getBoundingClientRect())}
                                    >
                                        <CollapseMenuButton
                                            icon={Icon}
                                            label={label}
                                            active={
                                                active === undefined
                                                    ? pathname.startsWith(href)
                                                    : active
                                            }
                                            submenus={submenus || []}
                                            isOpen={isOpen}
                                            href={href}
                                            onHover={handleHover}
                                            onHoverEnd={clearHover}
                                        />
                                    </div>
                                )
                            )}

                            {groupLabel === 'History' && isOpen && (
                                    <HistoryScrollable
                                        conversations={conversations}
                                        onHover={handleHover}
                                        onHoverEnd={clearHover}
                                        onEndReached={() => { if (hasMore) loadMore() }}
                                        hasMore={hasMore}
                                    />
                            )}
                        </li>
                    ))}
                </ul>
            </nav>
        </div>
    )
}

type BasicConversation = { id: string, title: string | null, updated_at?: string }

function HistoryScrollable({
    conversations,
    onHover,
    onHoverEnd,
    onEndReached,
    hasMore
}: {
    conversations: BasicConversation[]
    onHover: (rect: DOMRect) => void
    onHoverEnd: () => void
    onEndReached: () => void
    hasMore: boolean
}) {
    const groups: { label: string, items: BasicConversation[] }[] = groupByDate(conversations)
    const scrollRef = useRef<HTMLDivElement>(null)
    const [isAtTop, setIsAtTop] = useState(true)

    useEffect(() => {
        const el = scrollRef.current
        if (!el) return
        const onScroll = () => setIsAtTop(el.scrollTop === 0)
        // Set initial state
        onScroll()
        el.addEventListener('scroll', onScroll, { passive: true })
        return () => el.removeEventListener('scroll', onScroll)
    }, [])

    // Build flat list preserving grouped headings for infinite scroller rendering
    const flat: Array<{ type: 'header', id: string, label: string } | { type: 'item', id: string, conv: BasicConversation }> = []
    groups.forEach((g, gi) => {
        if (g.items.length === 0) return
        if (g.label !== '') flat.push({ type: 'header', id: `h-${gi}`, label: g.label })
        g.items.forEach((c) => flat.push({ type: 'item', id: c.id, conv: c }))
    })

    return (
        <div className="relative w-full min-h-0 flex-1">
            {/* Scroll container for history only */}
            <div id="sidebar-history-scroll" ref={scrollRef} className="h-full overflow-y-auto no-scrollbar pr-1">
                <InfiniteScroll
                    dataLength={flat.length}
                    next={onEndReached}
                    hasMore={hasMore}
                    loader={<div className="h-6" />}
                    scrollableTarget="sidebar-history-scroll"
                    style={{ overflow: 'visible' }}
                >
                    <div className="w-full">
                        {flat.map((entry, idx) => (
                            entry.type === 'header' ? (
                                <p key={`hdr-${entry.id}-${idx}`} className="text-xs text-muted-foreground px-0 pb-1 pt-1 mt-5 font-medium">{entry.label}</p>
                            ) : (
                                <div
                                    key={`itm-${entry.id}-${idx}`}
                                    className="w-full relative z-[1]"
                                    onMouseEnter={(e) => onHover((e.currentTarget as HTMLDivElement).getBoundingClientRect())}
                                    onMouseMove={(e) => onHover((e.currentTarget as HTMLDivElement).getBoundingClientRect())}
                                    onMouseLeave={() => onHoverEnd?.()}
                                >
                                    <Button
                                        variant={'ghost'}
                                        className="h-7 pl-1 justify-start items-center mb-0.5 rounded-md group w-full hover:bg-transparent"
                                        asChild
                                    >
                                        <Link href={`/chat/${entry.conv.id}`} className="w-full text-left pl-3">
                                            <span className="font-normal text-sm block truncate">
                                                {entry.conv.title || 'Untitled Chat'}
                                            </span>
                                        </Link>
                                    </Button>
                                </div>
                            )
                        ))}
                    </div>
                </InfiniteScroll>
            </div>
            
            {/* Gradient overlays */}
            <div className={cn("pointer-events-none absolute left-0 right-0 top-0 h-10 bg-gradient-to-b from-background4 to-transparent z-[5] ", isAtTop ? "opacity-0" : "opacity-100")} />
            <div className="pointer-events-none absolute left-0 right-0 bottom-0 h-12 bg-gradient-to-t from-background4 to-transparent z-[5]" />
        </div>
    )
}

function groupByDate(conversations: BasicConversation[]) {
    const result: { label: string, items: BasicConversation[] }[] = [
        { label: '', items: [] },
        { label: 'Yesterday', items: [] },
        { label: 'This week', items: [] },
        { label: 'Older', items: [] }
    ]

    for (const conv of conversations) {
        const dateStr = conv.updated_at
        const date = dateStr ? parseISO(dateStr) : null
        if (date && isToday(date)) {
            result[0].items.push(conv)
        } else if (date && isYesterday(date)) {
            result[1].items.push(conv)
        } else if (date && isThisWeek(date)) {
            result[2].items.push(conv)
        } else {
            result[3].items.push(conv)
        }
    }

    return result
}
